import { useState, useEffect } from 'react';
import { operacionesApi } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Loader2, Send, Mail } from 'lucide-react';

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export const EnvioGerenciaDialog = ({ open, onClose }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCfg(null);
    operacionesApi.getConfigEnvioAC()
      .then(r => setCfg({ ...r.data, destinatariosText: (r.data.destinatarios || []).join(', ') }))
      .catch(() => { toast.error('No se pudo cargar la configuración'); onClose(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDia = (d) => setCfg(c => ({
    ...c,
    dias: c.dias.includes(d) ? c.dias.filter(x => x !== d) : [...c.dias, d].sort(),
  }));

  const parseDest = () => cfg.destinatariosText.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await operacionesApi.saveConfigEnvioAC({
        enabled: cfg.enabled,
        destinatarios: parseDest(),
        dias: cfg.dias,
        hora: parseInt(cfg.hora, 10),
      });
      setCfg({ ...res.data, destinatariosText: (res.data.destinatarios || []).join(', ') });
      toast.success('Configuración de envío guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEnviarAhora = async () => {
    setSending(true);
    try {
      const res = await operacionesApi.enviarInformeAC();
      toast.success(res.data.message || 'Informe enviado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar el informe');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="envio-gerencia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Mail className="w-4 h-4" /> Envío automático a Gerencia</DialogTitle>
        </DialogHeader>
        {!cfg ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Envío automático activado</Label>
              <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg(c => ({ ...c, enabled: v }))} data-testid="switch-envio-enabled" />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Días de envío</Label>
              <div className="flex gap-1">
                {DIAS.map((d, i) => (
                  <button key={d} onClick={() => toggleDia(i)}
                    className={`px-2 py-1 rounded text-[11px] border transition-colors ${cfg.dias.includes(i) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-input hover:bg-muted'}`}
                    data-testid={`dia-${i}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Hora de envío (Chile)</Label>
              <select value={cfg.hora} onChange={(e) => setCfg(c => ({ ...c, hora: e.target.value }))}
                className="flex h-8 w-32 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="select-hora-envio">
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Destinatarios (separados por coma)</Label>
              <Textarea value={cfg.destinatariosText} onChange={(e) => setCfg(c => ({ ...c, destinatariosText: e.target.value }))}
                rows={2} className="text-xs" placeholder="gerencia@chirinosteel.com, ..." data-testid="input-destinatarios" />
            </div>

            {cfg.last_sent_date && (
              <p className="text-[11px] text-muted-foreground">Último envío automático: {cfg.last_sent_date}</p>
            )}

            <div className="flex justify-between pt-1">
              <Button variant="outline" size="sm" onClick={handleEnviarAhora} disabled={sending} className="gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-enviar-ahora">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Enviar ahora
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="btn-guardar-envio">
                {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Guardar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
